import React, { useEffect, useMemo } from 'react';
import './order-list-screen-content.scss';
import {
  APP_TYPE,
  CustomerData,
  OrderDataResponse,
  Product,
  TeamMember,
  getSearchParameter,
  useAppSelector,
  getSendType,
  getSelectedItemIds,
  getAppType,
} from '@zonofi/zono-money-store';
import {
  ChipFilters,
  EmptyStateView,
  EndListView,
  ListSkeleton,
  PullRefreshContainer,
  SpinnerLoader,
  searchComponents,
} from '../../components';
import { OrderCard } from '../order-card';
import { useOrderList } from '../hooks';
import {
  FIELD_OPS_ROUTES,
  SearchConfig,
  SELLER_WEBAPP_ROUTES,
} from '../../constants';
import { CommonButton } from '../../components';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { ChipFilterSection } from '../../types';
import { getTextFromRange } from '../../helper';

export interface OrderListScreenContentProps {
  setOrderData?: React.Dispatch<React.SetStateAction<OrderDataResponse[]>>;
  searchResult?:
    | OrderDataResponse[]
    | CustomerData[]
    | TeamMember[]
    | Product[];
  showSearch?: boolean;
  setShowSearch?: (val: boolean) => void;
  searchConfig?: SearchConfig;
  onDateChipPress?: () => void;
  isCustomer?: boolean;
}

export const OrderListScreenContent: React.FC<OrderListScreenContentProps> = ({
  setOrderData = () => {},
  searchResult = [],
  showSearch = false,
  setShowSearch = () => {},
  searchConfig = {} as SearchConfig,
  onDateChipPress = () => {},
  isCustomer = false,
}) => {
  const navigate = useNavigate();
  const {
    ordersData,
    onOrderItemPress,
    lastItemRef,
    emptyStateMap,
    lastPath,
    isLoading,
    onScroll,
    isEndOfList,
    appliedDateRange,
    statusFilters,
    updateStatusFilter,
    refreshOrderList,
  } = useOrderList({ isCustomer });

  const apptype = useAppSelector(getAppType);
  const searchParameter = useAppSelector(getSearchParameter);
  const sendType = useAppSelector(getSendType);
  const selectedOrderIds = useAppSelector(getSelectedItemIds);

  const dataToRender =
    searchParameter?.parameterId === 'searchKeyword' &&
    searchResult &&
    searchResult?.length > 0
      ? searchResult
      : ordersData;

  useEffect(() => {
    setOrderData((dataToRender as OrderDataResponse[]) ?? []);
  }, [dataToRender]);

  const searchProp =
    apptype === APP_TYPE.FIELDS_OPS
      ? { search: searchComponents({ searchConfig, setShowSearch }) }
      : undefined;

  const chips: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: '',
            value: getTextFromRange(appliedDateRange),
            selected: true,
            type: 'date',
          },
        ],
        onItemPress: onDateChipPress,
      },
      {
        items: statusFilters,
        onItemPress: ({ id }: { id: string }) => updateStatusFilter(id),
      },
    ],
    [appliedDateRange, statusFilters]
  );

  const onContinuePress = () => {
    const routeToNavigate =
      apptype === APP_TYPE.SELLER_HUB ? SELLER_WEBAPP_ROUTES : FIELD_OPS_ROUTES;
    navigate(routeToNavigate.VIEW_SELECTED_ORDERS);
  };

  return (
    <>
      <div className="order-list-content">
        <ChipFilters
          sections={chips}
          chipFilterStyles={{
            marginTop: '-12px',
            alignSelf: 'flex-start',
            width: '100%',
            position: 'sticky',
            top: '-16px',
            left: '-16px',
            ...(apptype === APP_TYPE.FIELDS_OPS &&
              searchConfig?.enabled && { padding: 0 }),
          }}
          showSearch={showSearch}
          {...(searchProp && searchProp)}
        />
        <PullRefreshContainer
          className="order-list-content-container"
          onScroll={onScroll}
          onRefresh={refreshOrderList}
        >
          {isLoading ? (
            <ListSkeleton className="order-list-screen-skeleton" />
          ) : isEmpty(dataToRender) ? (
            <EmptyStateView
              imageSrc={emptyStateMap[lastPath as string]?.image}
              message={emptyStateMap[lastPath as string]?.message}
            />
          ) : (
            <div className="order-list-view">
              {(dataToRender as OrderDataResponse[])?.map(
                (orderItem: OrderDataResponse) => (
                  <OrderCard
                    key={orderItem.id}
                    item={orderItem}
                    onOrderItemPress={onOrderItemPress}
                    showSelection={sendType?.length !== 0}
                  />
                )
              )}
              <div ref={lastItemRef} />
              {!isEndOfList && <SpinnerLoader />}
              {isEndOfList && <EndListView />}
            </div>
          )}
        </PullRefreshContainer>
      </div>
      {selectedOrderIds?.length > 0 && (
        <div className="continue-bottom-button">
          <CommonButton text="View Selected Orders" onPress={onContinuePress} />
        </div>
      )}
    </>
  );
};
